import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import SearchBar from '@/features/documents/components/search-bar';
import { PartnerPopulated } from '@/features/partners/api/get-partners';
import { ChangeAliasDialog } from '@/features/partners/components/change-alias-dialog';
import { PartnerTable } from '@/features/partners/components/pages/partner-table';
import { usePartnersFiltered } from '@/features/partners/hooks/use-partners-filtered';
import {
  HubFilter,
  HubFilterOption,
} from '@/shared/components/filter/hub-filter';
import { DefaultLayout } from '@/shared/components/layouts/default-layout';
import { LoadingIndicator } from '@/shared/components/loading-indicator';
import { PaginationBar } from '@/shared/components/pagination-bar';
import { EmptyTablePlaceholder } from '@/shared/components/table/empty-table-placeholder';
import {
  createHubFilterStore,
  useGenericActiveProjectHubFilterCount,
  useGenericHasProjectHubFilters,
} from '@/shared/hooks/use-hub-filter-store';
import { usePagination } from '@/shared/hooks/use-pagination';

export const usePartnerHubFilterStore = createHubFilterStore();
export const useActivePartnerHubFilterCount = () =>
  useGenericActiveProjectHubFilterCount(usePartnerHubFilterStore());
export const useHasPartnerHubFilters = () =>
  useGenericHasProjectHubFilters(usePartnerHubFilterStore());

const PAGE_SIZE = 10;

export const PartnerHubPage = () => {
  const { t } = useTranslation('partner', { keyPrefix: 'PartnerHub' });
  const { data, isLoading, isError } = usePartnersFiltered();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentPartner, setCurrentPartner] = useState<
    PartnerPopulated | undefined
  >(undefined);
  const { searchQuery, setSearchQuery } = usePartnerHubFilterStore();
  const hasFilters = useHasPartnerHubFilters();

  const { paginatedData, currentPage, totalPages, setCurrentPage } =
    usePagination({ data: data ?? [], pageSize: PAGE_SIZE });

  const tableColumns = [
    t('TableColumns.partner'),
    t('TableColumns.alias'),
    t('TableColumns.role'),
    t('TableColumns.projects_count'),
    t('TableColumns.partner_since'),
  ];

  return (
    <DefaultLayout
      title={t('title')}
      headerActions={[
        <SearchBar
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('search_placeholder')}
        />,
        <HubFilter
          {...usePartnerHubFilterStore()}
          activeCount={() => useActivePartnerHubFilterCount()}
          options={[
            HubFilterOption.PARTNER,
            HubFilterOption.ROLE,
            HubFilterOption.FILTER_RANGE,
          ].map((option) => ({
            option: option,
          }))}
        />,
      ]}
    >
      {isLoading ? (
        <LoadingIndicator />
      ) : !data || isError ? (
        <span>{t('Errors.fetch')}</span>
      ) : data.length === 0 ? (
        <EmptyTablePlaceholder
          title={hasFilters ? t('no_partners_found') : t('empty_state_title')}
          description={hasFilters ? undefined : t('empty_state_description')}
          columns={tableColumns}
          dottedBorder
        />
      ) : (
        <>
          <PartnerTable
            partners={paginatedData}
            onEdit={(partner) => {
              setCurrentPartner(partner);
              setDialogOpen(true);
            }}
          />
          <PaginationBar
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      <ChangeAliasDialog
        partner={currentPartner}
        isOpen={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setCurrentPartner(undefined);
        }}
      />
    </DefaultLayout>
  );
};
